if (!customElements.get('arista-pdp-info-drawer')) {
  class AristaPdpInfoDrawer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.layer = this.querySelector('[data-arista-pdp-drawer-layer]');
      this.drawer = this.querySelector('[data-arista-pdp-drawer]');
      this.drawerBody = this.querySelector('.arista-pdp-drawer__body');
      this.drawerTitle = this.querySelector('[data-arista-pdp-drawer-title]');
      this.triggers = Array.from(this.querySelectorAll('[data-arista-pdp-panel-trigger]'));
      this.externalTriggers = Array.from(document.querySelectorAll('[data-arista-pdp-external-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-arista-pdp-panel]'));
      this.closeButtons = Array.from(this.querySelectorAll('[data-arista-pdp-panel-close]'));
      this.purchaseButtons = Array.from(this.querySelectorAll('[data-arista-scroll-to-purchase]'));
      this.proofCards = Array.from(this.querySelectorAll('[data-arista-proof-card]'));
      this.productStoryHost = this.querySelector('[data-arista-product-story]');
      this.productStoryTemplate = this.querySelector('[data-arista-product-story-template]');
      this.activeTrigger = null;
      this.closeTimer = null;
      this.isOpen = false;

      this.externalCtaSelector = '.cta_group_preview, [id^="omega-cta-button"]';
      this.removeInjectedExternalCtas(document);
      this.externalCtaObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) this.removeInjectedExternalCtas(node);
          });
        });
      });
      this.externalCtaObserver.observe(document.body, { childList: true, subtree: true });

      if (!this.layer || !this.drawer || !this.drawerTitle || !this.triggers.length) return;

      this.externalTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.drawer.id);
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPurchaseClick = this.onPurchaseClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onTriggerClick));
      this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseClick));
      this.purchaseButtons.forEach((button) => button.addEventListener('click', this.onPurchaseClick));
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.onTriggerClick));
      this.closeButtons.forEach((button) => button.removeEventListener('click', this.onCloseClick));
      this.purchaseButtons.forEach((button) => button.removeEventListener('click', this.onPurchaseClick));
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.externalCtaObserver?.disconnect();
      this.proofAbortController?.abort();
      window.clearTimeout(this.proofFetchTimeout);
      if (this.isOpen) this.unlockPage();
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
    }

    onTriggerClick(event) {
      const trigger = event.currentTarget;
      this.openPanel(trigger.dataset.aristaPdpPanelTrigger, trigger);
    }

    onCloseClick() {
      this.closePanel();
    }

    onDocumentClick(event) {
      const externalTrigger = event.target.closest('[data-arista-pdp-external-trigger]');
      if (!externalTrigger || !document.contains(externalTrigger)) return;

      const panelName = externalTrigger.dataset.aristaPdpExternalTrigger;
      const selectedPanel = this.panels.find((panel) => panel.dataset.aristaPdpPanel === panelName);
      if (!selectedPanel) return;

      event.preventDefault();
      this.openPanel(panelName, externalTrigger);
    }

    onPurchaseClick() {
      this.closePanel({ restoreFocus: false });
      window.setTimeout(() => {
        const purchaseButton = document.querySelector('[id^="ProductSubmitButton-"]');
        if (!purchaseButton) return;
        purchaseButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        purchaseButton.focus({ preventScroll: true });
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 310);
    }

    openPanel(panelName, focusTrigger) {
      const selectedPanel = this.panels.find((panel) => panel.dataset.aristaPdpPanel === panelName);
      if (!selectedPanel) return;

      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      this.triggers.forEach((item) =>
        item.setAttribute('aria-expanded', item.dataset.aristaPdpPanelTrigger === panelName ? 'true' : 'false')
      );
      this.externalTriggers.forEach((item) =>
        item.setAttribute('aria-expanded', item.dataset.aristaPdpExternalTrigger === panelName ? 'true' : 'false')
      );
      this.panels.forEach((panel) => {
        panel.hidden = panel !== selectedPanel;
      });

      this.activeTrigger = focusTrigger;
      this.drawerTitle.textContent =
        focusTrigger?.dataset.panelTitle || selectedPanel.getAttribute('aria-label') || 'Arista product guide';
      if (this.drawerBody) this.drawerBody.scrollTop = 0;
      if (panelName === 'product') this.hydrateProductStory();
      if (panelName === 'why') this.loadProofMedia();

      if (!this.isOpen) {
        this.isOpen = true;
        this.layer.hidden = false;
        this.drawer.setAttribute('aria-hidden', 'false');
        this.lockPage();
        document.addEventListener('keydown', this.onKeydown);
        this.layer.getBoundingClientRect();
        window.requestAnimationFrame(() => {
          this.layer.classList.add('is-open');
          window.requestAnimationFrame(() => this.drawer.focus({ preventScroll: true }));
        });
      } else {
        this.drawer.focus({ preventScroll: true });
      }
    }

    closePanel(options = {}) {
      if (!this.isOpen) return;
      const restoreFocus = options.restoreFocus !== false;
      const triggerToRestore = this.activeTrigger;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (restoreFocus && triggerToRestore && document.contains(triggerToRestore)) {
        triggerToRestore.focus({ preventScroll: true });
      } else if (this.drawer.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      this.isOpen = false;
      this.layer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.externalTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      document.removeEventListener('keydown', this.onKeydown);
      this.unlockPage();

      const finishClose = () => {
        this.layer.hidden = true;
        this.closeTimer = null;
      };

      if (reduceMotion) {
        finishClose();
      } else {
        this.closeTimer = window.setTimeout(finishClose, 290);
      }
    }

    removeInjectedExternalCtas(root) {
      if (!root) return;

      if (root.matches?.(this.externalCtaSelector)) {
        root.remove();
        return;
      }

      root.querySelectorAll?.(this.externalCtaSelector).forEach((element) => element.remove());
    }

    hydrateProductStory() {
      if (this.productStoryHydrated || !this.productStoryHost || !this.productStoryTemplate) return;
      this.productStoryHost.appendChild(this.productStoryTemplate.content.cloneNode(true));
      this.productStoryHydrated = true;
    }

    async loadProofMedia() {
      if (this.proofRequested || !this.proofCards.length || !this.dataset.proofFeedEndpoint) return;
      this.proofRequested = true;
      this.proofAbortController = new AbortController();
      this.proofFetchTimeout = window.setTimeout(() => this.proofAbortController.abort(), 6000);

      try {
        const endpoint = new URL(this.dataset.proofFeedEndpoint);
        if (endpoint.protocol !== 'https:' || endpoint.hostname !== 'instafeed.nfcube.com') return;

        endpoint.searchParams.set('limit', '40');
        endpoint.searchParams.set('account', this.dataset.proofAccount || 'arista-kids-jewels.myshopify.com');
        endpoint.searchParams.set('fu', '0');
        endpoint.searchParams.set('fid', this.dataset.proofFeedId || '0');
        endpoint.searchParams.set('hash', this.dataset.proofPublicHash || '');
        endpoint.searchParams.set('locale', this.dataset.proofLocale || 'en');
        endpoint.searchParams.set('handle', '');

        const response = await fetch(endpoint.href, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: this.proofAbortController.signal,
        });
        if (!response.ok) return;

        const payload = await response.json();
        if (Number.parseInt(payload?.meta?.code, 10) !== 200 || !Array.isArray(payload?.data)) return;

        const usedPostIds = new Set();
        this.proofCards.forEach((card) => {
          const keywords = (card.dataset.proofKeywords || '')
            .toLowerCase()
            .split('|')
            .map((keyword) => keyword.trim())
            .filter(Boolean);
          if (!keywords.length) return;

          const post = payload.data.find((candidate) => {
            const postId = String(candidate?.id || '');
            const caption = String(candidate?.caption?.text || '').toLowerCase();
            return !usedPostIds.has(postId) && keywords.some((keyword) => caption.includes(keyword));
          });
          if (!post) return;

          const image = card.querySelector('[data-arista-proof-image]');
          const source = this.safeProofMediaUrl(post?.images?.standard_resolution?.url);
          if (!image || !source) return;

          usedPostIds.add(String(post.id || ''));
          const fallbackSource = image.currentSrc || image.src;
          image.addEventListener(
            'error',
            () => {
              image.src = fallbackSource;
            },
            { once: true }
          );
          image.removeAttribute('srcset');
          image.src = source;
          image.alt = image.dataset.proofAlt || image.alt;
        });
      } catch (_error) {
        // Keep the truthful local Arista images when the live feed is unavailable.
      } finally {
        window.clearTimeout(this.proofFetchTimeout);
      }
    }

    safeProofMediaUrl(value) {
      if (!value) return '';

      try {
        const url = new URL(value, window.location.origin);
        const hostname = url.hostname.toLowerCase();
        const allowed =
          url.protocol === 'https:' &&
          (hostname === 'cdn.shopify.com' ||
            hostname.endsWith('.shopifycdn.com') ||
            hostname.endsWith('.cdninstagram.com') ||
            hostname.endsWith('.fbcdn.net'));
        return allowed ? url.href : '';
      } catch (_error) {
        return '';
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePanel();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        this.drawer.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.drawer)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockPage() {
      const body = document.body;
      this.scrollPosition = window.scrollY;
      this.savedBodyStyles = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow
      };

      body.style.position = 'fixed';
      body.style.top = `-${this.scrollPosition}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      body.classList.add('arista-pdp-drawer-lock');
    }

    unlockPage() {
      if (!this.savedBodyStyles) return;
      const body = document.body;
      Object.entries(this.savedBodyStyles).forEach(([property, value]) => {
        body.style[property] = value;
      });
      body.classList.remove('arista-pdp-drawer-lock');
      window.scrollTo(0, this.scrollPosition || 0);
      this.savedBodyStyles = null;
    }
  }

  customElements.define('arista-pdp-info-drawer', AristaPdpInfoDrawer);
}
